import { useMemo, useState } from "react";
import { Box, Typography, Modal } from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";

type ImagePreviewBoxProps = {
  images?: string[];
  itemCount?: number; // orderItems.length
};

export const ImagePreviewBox = ({
  images = [],
  itemCount = 0,
}: ImagePreviewBoxProps) => {
  const { translateWithoutPrefix } = useTranslation();
  const [open, setOpen] = useState(false);

  /**
   * 🔹 Normalize images locally
   * - item başına 1 image
   * - boş / kirli data safe
   */
  const normalizedImages = useMemo(() => {
    if (!images.length || itemCount === 0) return [];

    if (itemCount === 1) {
      return [images[0]];
    }

    return images.slice(0, itemCount);
  }, [images, itemCount]);

  if (!normalizedImages.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        height="100%"
        width="100%"
        gap={1}
        flexWrap="wrap"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        sx={{ cursor: "pointer" }}
      >
        {normalizedImages.length > 1 ? (
          normalizedImages.slice(0, 2).map((src, i) => (
            <Box
              key={`${src}-${i}`}
              sx={{
                width: 48,
                height: 48,
                overflow: "hidden",
                borderRadius: 1,
                border: "1px solid #ddd",
                "&:hover img": { transform: "scale(1.1)" },
              }}
            >
              <img
                src={src}
                alt={`img-${i}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.3s ease",
                }}
              />
            </Box>
          ))
        ) : (
          <img
            src={normalizedImages[0]}
            alt="img-0"
            style={{
              width: 48,
              height: 48,
              objectFit: "cover",
              borderRadius: 4,
              border: "1px solid #ddd",
            }}
          />
        )}

        {normalizedImages.length > 2 && (
          <Typography variant="caption" color="text.secondary">
            +{normalizedImages.length - 2}
          </Typography>
        )}
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            maxWidth: normalizedImages.length === 1 ? 400 : 800,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2}>
            {translateWithoutPrefix("common.images")}
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns={
              normalizedImages.length === 1
                ? "1fr"
                : "repeat(auto-fill, minmax(200px, 1fr))"
            }
            gap={2}
          >
            {normalizedImages.map((src, i) => (
              <Box
                key={`${src}-${i}`}
                sx={{
                  width: "100%",
                  height: normalizedImages.length === 1 ? "auto" : 180,
                  overflow: "hidden",
                  borderRadius: 1,
                  border: "1px solid #ddd",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={src}
                  alt={`modal-img-${i}`}
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Modal>
    </>
  );
};
